// src/pages/Exams/ExamRegistrationNew.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import { useStudents } from '../../hooks/useStudents';
import { useSubjects } from '../../hooks/useSubjects';
import { useExamSessions } from '../../hooks/useExamSessions';
import { supabase } from '../../supabaseClient';

/**
 * Admin flow:
 * 1) Pick Student
 * 2) Pick Exam Session
 * 3) Enter session-level info (venue, total_fee_paid, payment_date, registration_status, remarks)
 * 4) Select subjects to register (disables those already in this session)
 * On Submit:
 *  - Upsert parent in student_exam_session using the entered parent fields
 *  - Compute attempt_no per (student, subject) = max + 1 (ignoring soft-deletes)
 *  - Bulk insert student_exam_info rows (registered, pending)
 */

const REG_STATUS = ['Registered', 'Deferred', 'Cancelled'];

function ExamRegistrationNew() {
  const navigate = useNavigate();

  // Reference data
  const { students = [], loading: studentsLoading } = useStudents?.() ?? { students: [], loading: false };
  const { subjects = [], isLoading: subjectsLoading } = useSubjects?.() ?? { subjects: [], isLoading: false };
  const { examSessions = [], loading: sessionsLoading } = useExamSessions?.() ?? { examSessions: [], loading: false };

  // Form state (identifiers)
  const [studentId, setStudentId] = useState('');
  const [sessionId, setSessionId] = useState('');

  // Parent-level fields (session registration info)
  const [registrationStatus, setRegistrationStatus] = useState('Registered');
  const [venue, setVenue] = useState('');
  const [totalFeePaid, setTotalFeePaid] = useState(''); // keep as string for input; cast on save
  const [paymentDate, setPaymentDate] = useState('');
  const [remarks, setRemarks] = useState('');

  // Subjects selection
  const [selected, setSelected] = useState(() => new Set());

  // UX state
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  // Already-registered (in this session) -> disabled checkboxes
  const [alreadyInSession, setAlreadyInSession] = useState(() => new Set());
  const [loadingExisting, setLoadingExisting] = useState(false);

  // Watch student + session => load existing children for this parent (if any)
  const loadExistingInSession = useCallback(async (stuId, sessId) => {
    setAlreadyInSession(new Set());
    if (!stuId || !sessId) return;
    setLoadingExisting(true);
    try {
      // Find existing parent
      const { data: parentRows, error: pErr } = await supabase
        .from('student_exam_session')
        .select('id, registration_status, venue, total_fee_paid, payment_date, remarks')
        .eq('student_id', stuId)
        .eq('exam_session_id', sessId)
        .eq('is_deleted', false)
        .limit(1);
      if (pErr) throw pErr;

      if (parentRows && parentRows.length > 0) {
        const parentId = parentRows[0].id;

        // If a parent exists, prefill its fields so admins can add more subjects to that same registration
        const p = parentRows[0];
        setRegistrationStatus(p.registration_status || 'Registered');
        setVenue(p.venue || '');
        setTotalFeePaid(p.total_fee_paid == null ? '' : String(p.total_fee_paid));
        setPaymentDate(p.payment_date || '');
        setRemarks(p.remarks || '');

        // Load child subjects for this parent
        const { data: children, error: cErr } = await supabase
          .from('student_exam_info')
          .select('subject_id')
          .eq('session_registration_id', parentId)
          .eq('is_deleted', false);
        if (cErr) throw cErr;

        const setIds = new Set((children || []).map((r) => String(r.subject_id)));
        setAlreadyInSession(setIds);

        // Also unselect anything that's already registered
        setSelected((prev) => {
          const next = new Set(prev);
          for (const sid of Array.from(next)) {
            if (setIds.has(String(sid))) next.delete(sid);
          }
          return next;
        });
      } else {
        // Reset parent fields for a brand-new parent
        setRegistrationStatus('Registered');
        setVenue('');
        setTotalFeePaid('');
        setPaymentDate('');
        setRemarks('');
        setAlreadyInSession(new Set());
      }
    } catch (e) {
      console.error(e);
      // non-fatal: show banner
      setFormError(e?.message || 'Failed to load existing registrations for this session.');
    } finally {
      setLoadingExisting(false);
    }
  }, []);

  useEffect(() => {
    setFormError('');
    setSelected(new Set()); // reset subject choices when either student/session changes
    loadExistingInSession(studentId, sessionId);
  }, [studentId, sessionId, loadExistingInSession]);

  const onToggleSubject = (sid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid);
      else next.add(sid);
      return next;
    });
  };

  const canSubmit = useMemo(() => {
    return !!studentId && !!sessionId && selected.size > 0 && !saving;
  }, [studentId, sessionId, selected, saving]);

  const prettyError = (e) => {
    const msg = e?.message || String(e);
    if (/duplicate key value/i.test(msg)) return 'Duplicate registration detected. Some selected subjects may already be registered in this session.';
    if (/permission denied/i.test(msg)) return 'You do not have permission to perform this action.';
    return msg;
  };

  // Compute attempt_no for an array of subject_ids with one query
  const fetchAttemptNumbers = useCallback(async (stuId, subjectIds) => {
    if (!subjectIds.length) return {};
    const { data, error } = await supabase
      .from('student_exam_info')
      .select('subject_id, attempt_no')
      .eq('student_id', stuId)
      .eq('is_deleted', false)
      .in('subject_id', subjectIds);
    if (error) throw error;

    // Reduce to max per subject
    const maxBySubject = {};
    for (const row of data || []) {
      const key = String(row.subject_id);
      const current = maxBySubject[key] ?? 0;
      maxBySubject[key] = Math.max(current, Number(row.attempt_no || 0));
    }
    // +1 for next attempt
    const next = {};
    for (const sid of subjectIds) {
      const key = String(sid);
      next[key] = (maxBySubject[key] ?? 0) + 1;
    }
    return next;
  }, []);

  // Upsert parent (find existing Registered or create new) AND set parent fields
  const findOrCreateParent = useCallback(
    async (stuId, sessId, fields) => {
      // Try to find existing
      const { data: found, error: fErr } = await supabase
        .from('student_exam_session')
        .select('id')
        .eq('student_id', stuId)
        .eq('exam_session_id', sessId)
        .eq('is_deleted', false)
        .limit(1);
      if (fErr) throw fErr;

      const parentPayload = {
        student_id: stuId,
        exam_session_id: sessId,
        registration_status: fields.registration_status || 'Registered',
        venue: fields.venue || null,
        total_fee_paid:
          fields.total_fee_paid === '' || fields.total_fee_paid == null
            ? null
            : Number(fields.total_fee_paid),
        payment_date: fields.payment_date || null,
        remarks: fields.remarks || null,
        is_deleted: false,
      };

      if (found && found.length > 0) {
        // Update existing parent with latest info from the form
        const parentId = found[0].id;
        const { error: uErr } = await supabase
          .from('student_exam_session')
          .update(parentPayload)
          .eq('id', parentId);
        if (uErr) throw uErr;
        return parentId;
      }

      // Create new
      const { data: created, error: cErr } = await supabase
        .from('student_exam_session')
        .insert(parentPayload)
        .select('id')
        .single();
      if (cErr) throw cErr;
      return created.id;
    },
    []
  );

  const handleCancel = () => navigate('/exam-registrations');

  const handleSubmit = async () => {
    setFormError('');
    if (!canSubmit) return;

    setSaving(true);
    try {
      // Guard: remove any subjects that are already registered in this session
      const chosen = Array.from(selected).map(String);
      const filtered = chosen.filter((sid) => !alreadyInSession.has(String(sid)));
      if (filtered.length === 0) {
        throw new Error('All selected subjects are already registered in this session.');
      }

      // 1) Parent upsert with the form's session-level fields
      const parentId = await findOrCreateParent(studentId, sessionId, {
        registration_status: registrationStatus,
        venue,
        total_fee_paid: totalFeePaid,
        payment_date: paymentDate,
        remarks,
      });

      // 2) Compute attempt_no per subject (single query)
      const attemptsMap = await fetchAttemptNumbers(studentId, filtered);

      // 3) Build child rows
      const childRows = filtered.map((sid) => ({
        session_registration_id: parentId,
        student_id: studentId,
        subject_id: sid,
        attempt_no: attemptsMap[String(sid)] ?? 1,
        registration_status: 'Registered',
        // Guard rules: result always 'pending' on creation
        result_status: 'Pending',
        is_deleted: false,
      }));

      // 4) Bulk insert children
      const { error: insErr } = await supabase.from('student_exam_info').insert(childRows);
      if (insErr) throw insErr;

      alert('Exam registration created successfully.');
      navigate('/exam-registrations');
    } catch (e) {
      console.error(e);
      setFormError(prettyError(e));
    } finally {
      setSaving(false);
    }
  };

  // Derived lists
  const studentsOptions = useMemo(
    () =>
      (students || []).map((s) => ({ id: s.id, label: s.full_name || s.name || '—' })),
    [students]
  );
  const sessionsOptions = useMemo(
    () => (examSessions || []).map((s) => ({ id: s.id, label: s.name })), 
    [examSessions]
  );

  const subjectRows = useMemo(() => {
    // Sort by code/name if your useSubjects provides them
    return (subjects || []).map((subj) => ({
      id: subj.id,
      code: subj.code || subj.subject_code || '',
      name: subj.name || subj.subject_name || '',
    }));
  }, [subjects]);

  const disabledCount = alreadyInSession.size;

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="space-y-6">
        <h1 className="text-xl font-bold text-gray-900">New Exam Registration</h1>

        <Card>
          <div className="mb-4 text-sm text-gray-600">
            Select a student and an exam session, fill in session-level details, then choose one or more subjects to register.
            Subjects already registered for this session will be disabled.
          </div>

          {formError && (
            <div className="mb-4 p-3 rounded bg-red-100 text-red-700">{formError}</div>
          )}

          {/* Student & Session */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Student */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Student *
              </label>
              <select
                value={studentId}
                onChange={(e) => setStudentId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                disabled={studentsLoading || saving}
              >
                <option value="">Select a student</option>
                {studentsOptions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Exam Session */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Exam Session *
              </label>
              <select
                value={sessionId}
                onChange={(e) => setSessionId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                disabled={sessionsLoading || saving}
              >
                <option value="">Select a session</option>
                {sessionsOptions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Parent Details (session-level) */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Registration Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Registration Status
              </label>
              <select
                value={registrationStatus}
                onChange={(e) => setRegistrationStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                disabled={saving}
              >
                {REG_STATUS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {/* Venue */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Venue
              </label>
              <Input
                value={venue}
                onChange={(e) => setVenue(e.target.value)}
                disabled={saving}
                placeholder="e.g., KL, SABAH"
              />
            </div>

            {/* Total Fee Paid */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fee Paid (optional)
              </label>
              <Input
                type="number"
                step="0.01"
                value={totalFeePaid}
                onChange={(e) => setTotalFeePaid(e.target.value)}
                disabled={saving}
                placeholder="e.g., 0.00"
              />
            </div>

            {/* Payment Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Date (optional)
              </label>
              <Input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                disabled={saving}
              />
            </div>

            {/* Remarks (full width on large screens) */}
            <div className="md:col-span-2 lg:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Remarks
              </label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={3}
                disabled={saving}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Optional remarks…"
              />
            </div>
          </div>

          {/* Subjects */}
          <div className="mt-8">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold">Subjects</h2>
              <div className="text-xs text-gray-500">
                {loadingExisting ? 'Checking existing…' : disabledCount > 0 ? `${disabledCount} already registered in this session` : null}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
              {subjectsLoading ? (
                <div className="col-span-full text-gray-600">Loading subjects…</div>
              ) : subjectRows.length === 0 ? (
                <div className="col-span-full text-gray-600">No subjects found.</div>
              ) : (
                subjectRows.map((subj) => {
                  const sid = String(subj.id);
                  const disabled = alreadyInSession.has(sid) || !studentId || !sessionId || saving;
                  const checked = selected.has(sid);
                  return (
                    <label key={sid} className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${disabled ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'}`}>
                      <input
                        type="checkbox"
                        disabled={disabled}
                        checked={checked}
                        onChange={() => onToggleSubject(sid)}
                      />
                      <div className="text-sm">
                        <div className="font-medium text-gray-900">{subj.code || subj.name}</div>
                        {subj.code && subj.name && (
                          <div className="text-gray-600">{subj.name}</div>
                        )}
                        {alreadyInSession.has(sid) && (
                          <div className="text-xs text-gray-500">Already registered in this session</div>
                        )}
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex gap-2 justify-end">
            <Button variant="outline" onClick={handleCancel} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit}>
              {saving ? 'Saving…' : 'Create Registration'}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default ExamRegistrationNew;
